/**
 * Crawl Strategy Management
 * Light sources, stealth mode, and vision computation.
 */

import type { StretchState, LightSource } from './types';

// ============================================================================
// LIGHT SOURCES
// ============================================================================

/** Add a light source to the crawl strategy. */
export function addLightSource(state: StretchState, source: LightSource): StretchState {
  return {
    ...state,
    crawlStrategy: {
      ...state.crawlStrategy,
      lightSources: [...state.crawlStrategy.lightSources, source],
    },
  };
}

/** Remove a light source by ID. */
export function removeLightSource(state: StretchState, sourceId: string): StretchState {
  return {
    ...state,
    crawlStrategy: {
      ...state.crawlStrategy,
      lightSources: state.crawlStrategy.lightSources.filter(s => s.id !== sourceId),
    },
  };
}

// ============================================================================
// STEALTH MODE
// ============================================================================

/** Toggle party stealth mode. */
export function toggleStealthMode(state: StretchState): StretchState {
  return {
    ...state,
    crawlStrategy: {
      ...state.crawlStrategy,
      stealthMode: !state.crawlStrategy.stealthMode,
    },
  };
}

// ============================================================================
// VISION COMPUTATION
// ============================================================================

/**
 * Compute effective vision radius for a player.
 * Base vision + any carried light source radius.
 * If no light sources and base vision is 0 (darkness), player sees nothing.
 */
export function computePlayerVision(
  baseVisionRadius: number,
  lightSources: LightSource[],
  playerId: string,
): number {
  // Find the best light source carried by this player
  const carriedLight = lightSources
    .filter(s => s.carrierId === playerId && s.remainingStretches > 0)
    .reduce((best, s) => Math.max(best, s.radius), 0);

  // Find any nearby light sources (carried by other players) — simplified:
  // in a party-based dungeon crawl, all light sources contribute to shared visibility
  const anyLight = lightSources
    .filter(s => s.remainingStretches > 0)
    .reduce((best, s) => Math.max(best, s.radius), 0);

  // Player uses their own carried light, or the party's best if nearby
  const effectiveLight = Math.max(carriedLight, anyLight);

  return Math.max(baseVisionRadius, effectiveLight);
}

/** Create a default light source. */
export function createLightSource(
  id: string,
  carrierId: string,
  type: LightSource['type'],
): LightSource {
  const defaults: Record<LightSource['type'], { radius: number; stretches: number }> = {
    torch: { radius: 8, stretches: 6 },       // ~1 hour
    lantern: { radius: 10, stretches: 36 },    // ~6 hours
    magic: { radius: 12, stretches: Infinity }, // Until dispelled
    other: { radius: 6, stretches: 12 },       // ~2 hours
  };

  const def = defaults[type];
  return {
    id,
    carrierId,
    type,
    radius: def.radius,
    remainingStretches: def.stretches,
  };
}
